import {
  AccessTimeOutlined,
  PlaceOutlined,
  SchoolOutlined,
} from '@mui/icons-material';
import CurrencyExchangeOutlined from '@mui/icons-material/CurrencyExchangeOutlined';
import { Box, Stack, Typography, type SvgIconProps } from '@mui/material';

import { fontWeights } from '@/@mui/tokens/font-weights';
import { ToggleButton } from '@/components/shared/toggle-button-group';
import { mapResourceType } from '@/constants/resources/map-resource-type';
import {
  externalResourceIconNames,
  resourceIconMap,
} from '@/constants/resources/resources-icons';
import type { ResourceApproval } from '@/data/subsidy/interfaces';
import type { ResourceTypesLabels } from '@/models/resources';
import { formatDurationWithModality } from '@/utils/format-duration-with-modality';
import { formatMoney } from '@/utils/format-money';
import { lineClamp } from '@/utils/line-clamp';

interface ResourceApprovalItemProps {
  resourceApproval: ResourceApproval;
}

const getCategoryIcon = (
  category: ResourceTypesLabels,
  size: SvgIconProps['fontSize'] = 'small'
) => {
  const resourceType = mapResourceType[category];
  const iconName = externalResourceIconNames[resourceType];
  const Icon = resourceIconMap[iconName] || SchoolOutlined;
  return <Icon fontSize={size} />;
};

export const ResourceApprovalItem = ({
  resourceApproval,
}: ResourceApprovalItemProps) => {
  const resource = resourceApproval.resource;

  const formattedDuration = formatDurationWithModality(
    resource.course?.duration,
    resource.course?.duration_type,
    resource.course?.modality
  );

  const policy = resourceApproval.policies?.[0];

  return (
    <ToggleButton value={resourceApproval?.uuid}>
      <Stack
        direction="row"
        gap={2}
        alignItems="flex-start"
        width="100%"
        color="neutral.700"
      >
        {getCategoryIcon(resourceApproval.category, 'medium')}
        <Stack gap={2} flex={1}>
          <Typography variant="h3" sx={lineClamp(2)} lineHeight={1.6}>
            {resourceApproval?.name}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={2}
            alignItems={{ sm: 'center' }}
          >
            {resourceApproval?.sale_price && (
              <Stack direction="row" gap={0.5} alignItems="center">
                <CurrencyExchangeOutlined fontSize="small" />
                <Typography variant="body2" color="neutral.700">
                  {formatMoney(resourceApproval.sale_price)}
                </Typography>
              </Stack>
            )}

            {resource.course?.institution_name && (
              <Stack direction="row" gap={0.5} alignItems="center">
                <PlaceOutlined fontSize="small" />
                <Typography variant="body2" color="neutral.700">
                  {resource.course.institution_name}
                </Typography>
              </Stack>
            )}

            {resourceApproval?.category && (
              <Stack direction="row" gap={0.5} alignItems="center">
                {getCategoryIcon(resourceApproval.category)}
                <Typography variant="body2" color="neutral.700">
                  {resourceApproval.category}
                </Typography>
              </Stack>
            )}

            {formattedDuration && (
              <Stack direction="row" gap={0.5} alignItems="center">
                <AccessTimeOutlined fontSize="small" />
                <Typography variant="body2" color="neutral.700">
                  {formattedDuration}
                </Typography>
              </Stack>
            )}
          </Stack>

          {policy && (
            <Typography variant="body2" sx={lineClamp(1)} color="neutral.600">
              <Box component="span" fontWeight={fontWeights.fontWeightSemibold}>
                Política:{' '}
              </Box>
              {policy.name}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ToggleButton>
  );
};
